import { useForm } from 'react-hook-form';
import { FormProvider, SelectTag, Table, TextField } from '../../../../../components';
import {
  Button,
  Container,
  Dialog,
  Divider,
  Grid,
  IconButton,
  Paper,
  Stack,
  Typography,
  TextField as MuiTextField,
  Box
} from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { dropdownFetch, getCurrentDateTime, getKeyPathValue, request, toast } from '../../../../../utils';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation } from 'react-router-dom';
import { CustomButtonn } from '../../../../../components';
import { getDateDifference } from '../../../../../utils';
import { IPDSearchHandler } from './utils';

const TRANSFER_HEADER = [
  {
    header: 'IPD No.',
    accessor: 'ipd_no'
  },
  {
    header: 'Transfer Date',
    accessor: (row) => `${getCurrentDateTime('date', row.transfer_date)}`
  },
  {
    header: 'Old Doctor',
    accessor: 'old_doctor'
  },
  {
    header: 'Old Department',
    accessor: 'old_department'
  },
  {
    header: 'New Doctor',
    accessor: 'transfer_doctor.name'
  },
  {
    header: 'New Department',
    accessor: 'transfer_department.department'
  },
  {
    header: 'Status',
    accessor: 'status',
    sx: { textTransform: 'uppercase' }
  }
];

const TABLE_HEADER = [
  {
    header: 'IPD No. ',
    accessor: (row) => {
      const status = row.status;
      return (
        <span
          style={{
            color: status === 'inactive' ? 'red' : 'black'
          }}
        >
          {`${row.ipd_no}`}
        </span>
      );
    }
  },
  {
    header: 'From Doctor',
    accessor: 'old_doctor'
  },

  {
    header: 'To Doctor',
    accessor: 'transfer_doctor.name'
  },
  {
    header: 'Transfer Date',
    accessor: (row) => `${getCurrentDateTime('date', row.transfer_date)}`
  }
];

const initialConsultantTransferDataObj = {
  data: [],
  loading: false
};

export const ConsultantTransfer = () => {
  const { getValues } = useForm();
  const doctorIDRef = useRef();
  const [detailList, setDetailList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentAction, setCurrentAction] = useState();
  const [selectedRecord, setSelectedRecord] = useState();
  const [consultantTransferObj, setConsultantTransferObj] = useState(initialConsultantTransferDataObj);
  const [openQueryDialog, setOpenQueryDialog] = useState(false);
  const { pathname } = useLocation();
  const [IPDSearchDialogOpen, setIPDSearchDialogOpen] = useState(false);
  const lastPathSegment = pathname.split('/').pop();
  const pathParts = pathname.split('/');
  const organisation = pathParts[1];
  const [doctorDropdown, setDoctorDropdown] = useState([]);
  const [departmentDropdown, setDepartmentDropdown] = useState([]);
  const [input, setInput] = useState('');
  const [patientQueryData, setPatientQueryData] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const user = JSON.parse(localStorage.getItem('user'));

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        remarks: Yup.string().required(' ')
      })
    ),
    defaultValues: {
      date: getCurrentDateTime('date'),
      time: getCurrentDateTime('time')
    },
    mode: 'all'
  });
  const { handleSubmit, watch, reset, setValue } = methods;

  const consultantWatch = watch('old_doctor');
  const newDoctorWatch = watch('transfer_doctor');
  const extractDoctorName = doctorDropdown.find((doctor) => doctor?._id === newDoctorWatch)?.name;

  const formSubmit = async (formValues) => {
    setLoading(true);
    let editFlag = false;
    if (consultantWatch === extractDoctorName) {
      toast(`Cannot Transfer the Patient To the Same Doctor`, { variant: 'warning' });
      return;
    }
    if (selectedRecord && currentAction === 'edit') {
      editFlag = true;
    }
    try {
      const response = await request(editFlag ? '/consultant-transfer-edit' : '/consultant-transfer-create', {
        method: editFlag ? 'PUT' : 'POST',
        params: editFlag ? selectedRecord._id : null,
        body: {
          ...formValues,
          createdBy: user.name,
          updatedBy: editFlag ? user.name : '',
          organisation
        }
      });

      if (response && response.success) {
        toast(`Successfully ${editFlag ? 'edited' : 'created'} Consultant Transfer`, { variant: 'success' });
        setSelectedRecord(response.data);
        setCurrentAction();
        handleList();
      } else {
        toast(`Failed to ${editFlag ? 'edit' : 'create'} Consultant Transfer`, { variant: 'error' });
      }
    } catch (error) {
      console.error('An error occurred during form submission:', error);
      toast(`An error occurred while trying to ${editFlag ? 'edit' : 'create'} Consultant Transfer`, {
        variant: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRecord) {
      const keyValuePair = getKeyPathValue(selectedRecord, '', ['_id', 'status', '__v']);
      keyValuePair.forEach(({ key, value }) => setValue(key, value));
    } else {
      reset();
    }
  }, [setValue, reset, selectedRecord]);

  const handleCancelRecord = async () => {
    setLoading(true);
    const response = await request('/consultant-transfer-cancel', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully cancel Consultant Transfer', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to cancel Consultant Transfer', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleDeleteRecord = async () => {
    setLoading(true);
    const response = await request('/consultant-transfer-delete', {
      method: 'DELETE',
      params: selectedRecord._id
    });
    if (response?.success) {
      toast('Successfully deleted Consultant Transfer', { variant: 'success' });
      setSelectedRecord();
    } else {
      toast('Failed to delete Consultant Transfer', { variant: 'error' });
    }
    setLoading(false);
  };

  const getDoctorDropdown = useCallback(async () => {
    const data = await dropdownFetch('doctor');
    if (!selectedRecord && !getValues('transfer_doctor')) {
      const [defaultState] = data.filter(({ status }) => status === 'default');
      setValue('transfer_doctor', defaultState?._id);
      if (defaultState) {
        setValue('transfer_department', defaultState.qualification_details.department);
      }
    }

    setDoctorDropdown(data);
  }, [selectedRecord, getValues, setValue]);

  const getDepartmentDropdown = useCallback(async () => {
    const data = await dropdownFetch('department');
    setDepartmentDropdown(data);
  }, []);

  useEffect(() => {
    getDoctorDropdown();
    getDepartmentDropdown();
  }, [getDoctorDropdown, getDepartmentDropdown]);

  const handleQueryWindow = async () => {
    setOpenQueryDialog(true);
    setConsultantTransferObj({ loading: true });
    const response = await request('/consultant-transfer-list', { method: 'GET' });
    if (response.success) {
      const filteredData = response.data.filter(
        (item) => item.status === 'active' || item.status === 'inactive' || item.status === 'default'
      );
      setConsultantTransferObj({ loading: false, data: filteredData });
    } else {
      toast('Error in fetching data', { variant: 'error' });
      setConsultantTransferObj({ loading: false });
    }
  };

  const calculateDateDifference = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const differenceInTime = end - start;
    const differenceInDays = Math.ceil(differenceInTime / (1000 * 3600 * 24));
    return differenceInDays;
  };

  const searchIPDDetails = async (ipd_no) => {
    setLoading(true);
    const response = await request('/indoor-registeration-detail', {
      method: 'GET',
      params: ipd_no
    });
    if (response?.success) {
      if (!response?.data) {
        toast('No IPD found', { variant: 'warning' });
      } else {
        const { data: patientData } = response;
        console.log('hello DATA', patientData);
        setValue('ipd_no', ipd_no);
        setValue('patient_name', `${patientData.patient.first_name} ${patientData.patient.last_name}`);
        setValue('patient_suffix', patientData.patient.name_suffix);
        setValue('ward', patientData.consultant_detail.room?.ward?.name);
        setValue('uhid', patientData.uhid);
        setValue('old_department', patientData.consultant_detail?.department?.department);
        const { year } = getDateDifference(new Date(patientData.patient.dob));
        setValue('age', year);
        doctorIDRef.current = patientData.patient.consultant?._id;
        setValue('old_doctor', patientData.consultant_detail?.doctor?.name);
        setValue('room_no', patientData.consultant_detail?.room?.room_no);
        setValue('bed_no', patientData.consultant_detail?.bed_no);
        setValue('sponsor', patientData.patient.sponsor?.name);
        const today = new Date();
        const admissionDate = patientData.admissionDate;
        const dayStay = calculateDateDifference(admissionDate, today);
        setValue('day_stay', dayStay);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

  const handleList = async () => {
    setLoading(true);
    const response = await request('/consultant-transfer-list', {
      method: 'GET'
    });
    if (response?.success) {
      if (!response?.data) {
        toast('No Record Found ', { variant: 'warning' });
      } else {
        const sortedData = response.data.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        setDetailList(sortedData);
        console.log('HELLO LIST', detailList);
      }
    } else {
      toast('Failed to load patient details', { variant: 'error' });
    }
    setLoading(false);
  };

  useEffect(() => {
    handleList();
  }, []);

  useEffect(() => {
    if (consultantTransferObj.data && consultantTransferObj.data.length > 0) {
      setPatientQueryData(consultantTransferObj.data);
      setIsFiltered(false);
    }
  }, [consultantTransferObj.data]);

  const handlePatientData = () => {
    const lowercasedInput = input.toLowerCase();
    const filtered = consultantTransferObj.data.filter((item) =>
      item.ipd_no?.toString().toLowerCase().includes(lowercasedInput)
    );
    setPatientQueryData(filtered);
  };

  const handleChange = (e) => {
    const newInput = e.target.value;
    setInput(newInput);
    handlePatientData();
  };

  return (
    <>
      <Paper
        sx={{
          p: 0,
          flex: 1,
          borderRadius: 0,
          boxShadow: 'none',
          width: '100%',
          height: 'auto'
        }}
      >
        <Container
          sx={{
            mt: 1,
            border: '0px solid',
            borderColor: 'grey.300',
            borderRadius: 1,
            width: '100%',
            maxWidth: '100vw',
            boxSizing: 'border-box',
            minWidth: '100%'
          }}
        >
          <FormProvider methods={methods} onSubmit={handleSubmit(formSubmit)}>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="space-between"
              alignItems="center"
              sx={{
                mb: 1,
                borderBottom: '0px solid',
                borderColor: 'grey.300'
              }}
            >
              <Typography color="primary" variant="h3" sx={{ fontWeight: 600 }}>
                Consultant Transfer Management
              </Typography>
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 3 }} spacing={1}>
                <CustomButtonn
                  id={`${lastPathSegment}Write`}
                  variant="contained"
                  disabled={['edit', 'new'].includes(currentAction)}
                  onClick={() => {
                    setSelectedRecord();
                    setCurrentAction('new');
                    reset();
                  }}
                >
                  New
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Update`}
                  variant="contained"
                  disabled={['new', 'edit'].includes(currentAction) || !selectedRecord}
                  onClick={() => setCurrentAction('edit')}
                >
                  Edit
                </CustomButtonn>
                <CustomButtonn
                  id={'Submit'}
                  type="submit"
                  variant="contained"
                  disabled={!['edit', 'new'].includes(currentAction) || loading}
                >
                  Save
                </CustomButtonn>
                <CustomButtonn
                  id={'Cancel'}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                  onClick={() => {
                    setCurrentAction();
                    handleCancelRecord();
                  }}
                >
                  Cancel
                </CustomButtonn>
                <CustomButtonn
                  id={`isSuperAdmin`}
                  variant="contained"
                  disabled={!selectedRecord || ['new', 'edit'].includes(currentAction) || loading}
                  onClick={handleDeleteRecord}
                >
                  Delete
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Read`}
                  variant="contained"
                  onClick={handleQueryWindow}
                  disabled={['new', 'edit'].includes(currentAction)}
                >
                  Query
                </CustomButtonn>
                <CustomButtonn
                  id={`${lastPathSegment}Print`}
                  variant="contained"
                  disabled={!selectedRecord || currentAction}
                >
                  Print
                </CustomButtonn>
                <CustomButtonn
                  id={'Exit'}
                  variant="contained"
                  onClick={() => {
                    setCurrentAction('');
                  }}
                >
                  Exit
                </CustomButtonn>
              </Stack>
            </Stack>
            <Box
              sx={{
                border: '0px solid gray',
                padding: 2,
                borderRadius: '8px',
                width: '80%',
                boxShadow: '0px 2px 10px #0099ff'
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={6} sx={{ display: 'flex', gap: 0.2, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>Search Adm. No.</Typography>
                    <TextField disabled sx={{ flex: 0.7 }} name="ipd_no" placeholder="Search By Adm No." />
                    <Button
                      disabled={!['new'].includes(currentAction)}
                      sx={{ flex: 0.1, minWidth: 0, p: 0.15, height: '100%' }}
                      variant="contained"
                      onClick={() => setIPDSearchDialogOpen(true)}
                    >
                      <SearchIcon />
                    </Button>
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>UHID</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} placeholder="Uhid No" name="uhid" />
                  </Stack>

                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>Doctor</Typography>
                    <TextField
                      sx={{ flex: 0.8 }}
                      placeholder="Doctor"
                      name="old_doctor"
                      disabled
                      menus={doctorDropdown}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>Department</Typography>
                    <TextField
                      sx={{ flex: 0.8 }}
                      name="old_department"
                      disabled
                      placeholder="department"
                      menus={departmentDropdown}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>Day Stay</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} placeholder="Day Stay In Perv Room" name="day_stay" />
                  </Stack>
                </Grid>
                <Grid item xs={6} sx={{ display: 'flex', gap: 0.2, flexDirection: 'column' }}>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>Date/Time</Typography>
                    <TextField disabled sx={{ flex: 0.25 }} name="date" />
                    <TextField disabled sx={{ flex: 0.55 }} name="time" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>Patient Name</Typography>
                    <TextField disabled sx={{ flex: 0.8 }} placeholder="Patient Name" name="patient_name" />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>Transfer Doctor</Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      disabled={!['new', 'edit'].includes(currentAction)}
                      name="transfer_doctor"
                      placeholder="Transfer Doctor"
                      menus={doctorDropdown}
                      onChange={({ target: { row } }) => {
                        setValue('transfer_department', row.qualification_details.department);
                      }}
                    />
                  </Stack>

                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>
                      Transfer Department
                    </Typography>
                    <SelectTag
                      formControlSX={{ flex: 0.8 }}
                      name="transfer_department"
                      disabled
                      placeholder="Transfer Department"
                      menus={departmentDropdown.map((data) => ({ ...data, name: data.department }))}
                    />
                  </Stack>
                  <Stack spacing={0.5} direction="row" alignItems="center">
                    <Typography sx={{ flex: 0.25, fontWeight: 700, fontSize: '1.4rem' }}>Remarks</Typography>
                    <TextField
                      sx={{ flex: 0.8 }}
                      name="remarks"
                      disabled={!['new'].includes(currentAction)}
                      placeholder="Remarks"
                    />
                  </Stack>
                </Grid>
                <Grid container justifyContent="flex-end" marginLeft={'10px'}>
                  <Grid item xs={12} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Grid item xs={1.5}>
                      <Typography sx={{ fontWeight: 700, fontSize: '1.4rem' }}>Transfer History</Typography>
                      <Divider />
                    </Grid>
                    <Table
                      containerSX={{ flex: 1, border: '0px solid rgba(0,0,0,0.27)' }}
                      data={detailList}
                      columns={TRANSFER_HEADER}
                      size="small"
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          </FormProvider>
        </Container>
      </Paper>
      <Dialog
        open={openQueryDialog}
        onClose={() => setOpenQueryDialog(false)}
        PaperProps={{ sx: { width: '80%', p: 2 } }}
        maxWidth="xl"
        scroll="body"
      >
        <Stack
          padding={0}
          direction="row"
          spacing={2}
          alignItems="center"
          justifyContent="space-between"
          borderBottom="1px solid"
          borderColor="grey.300"
        >
          <Typography variant="h4" fontWeight="bold">
            Consultant Transfer Query Window
          </Typography>
          <IconButton onClick={() => setOpenQueryDialog(false)}>
            <CloseIcon color="error" fontSize="large" />
          </IconButton>
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mt: 0 }}>
          <MuiTextField
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '0',
                fontSize: '1.4rem' // Adjust input font size
              },
              '& .MuiOutlinedInput-input': {
                padding: '8px 1.4rem'
              },
              '& .MuiInputLabel-root': {
                fontSize: '1.2rem' // Adjust label font size
              }
            }}
            label="Search Patient"
            variant="outlined"
            value={input}
            onChange={handleChange}
          />
        </Stack>
        <Table
          sx={{ mt: 0.5 }}
          columns={TABLE_HEADER}
          data={patientQueryData || []}
          loading={consultantTransferObj.loading}
          onRowClick={(row) => {
            const { transfer_doctor, transfer_department, ...otherDetails } = row;
            setSelectedRecord({
              ...otherDetails,
              transfer_doctor: transfer_doctor?._id,
              transfer_department: transfer_department?._id
            });
            setOpenQueryDialog(false);
          }}
        />
      </Dialog>
      <Dialog
        PaperProps={{ sx: { p: 1 } }}
        open={IPDSearchDialogOpen}
        onClose={() => setIPDSearchDialogOpen(false)}
        scroll="body"
        maxWidth="xl"
      >
        <IPDSearchHandler key={String(IPDSearchDialogOpen)} onClose={() => setIPDSearchDialogOpen(false)} onAdd={searchIPDDetails} />
      </Dialog>
    </>
  );
};
